import { Box, TextField } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { VendorOnboardingForm } from '../../interfaces'
import { validateEmail } from '../../validators'
import GstCertificateDropzone from '../GstCertificateDropzone'
import { FieldGrid, SectionLabel, StageHeading, textFieldSx } from '../StepChrome'

interface Props {
    /** Set when a restored draft lost its attachment (a File can't be serialised). */
    needsReattach: boolean
}

const AgreementStep = ({ needsReattach }: Props) => {
    const { control } = useFormContext<VendorOnboardingForm>()

    return (
        <Box>
            <StageHeading
                title="Agreement"
                blurb="Your GST certificate and who signs on the company's behalf."
            />

            <SectionLabel>GST certificate</SectionLabel>
            <Controller
                control={control}
                name="gstCertificate"
                rules={{
                    validate: (file) =>
                        file ? true : 'Please attach your GST certificate',
                }}
                render={({ field, fieldState }) => (
                    <GstCertificateDropzone
                        value={field.value}
                        onChange={field.onChange}
                        error={
                            fieldState.error?.message ??
                            (needsReattach && !field.value
                                ? 'Your draft was restored, but the certificate could not be saved. Please re-attach it.'
                                : undefined)
                        }
                    />
                )}
            />

            <SectionLabel>Signing authority</SectionLabel>
            <FieldGrid>
                <Controller
                    control={control}
                    name="signingAuthorityName"
                    rules={{ required: 'Signing authority name is required' }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            label="Signing authority name *"
                            fullWidth
                            sx={textFieldSx}
                            error={Boolean(fieldState.error)}
                            helperText={
                                fieldState.error?.message ??
                                'The person authorised to sign the agreement'
                            }
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="signingAuthorityEmail"
                    rules={{ validate: validateEmail }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            label="Signing authority email *"
                            type="email"
                            fullWidth
                            sx={textFieldSx}
                            error={Boolean(fieldState.error)}
                            helperText={
                                fieldState.error?.message ??
                                'May differ from your own email'
                            }
                        />
                    )}
                />
            </FieldGrid>
        </Box>
    )
}

export default AgreementStep
